/**
 * The jQuery.ajax style callback API on top of howspace.ajax, for the callers that have not moved to it yet.
 * The requests keep the wire format jQuery.ajax gave them: the same body encoding, Content-Type and Accept headers.
 */
(function (real) {
    'use strict';

    const FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded; charset=UTF-8';
    const ACCEPT_HEADERS = {
        '*': '*/*',
        text: 'text/plain, */*; q=0.01',
        json: 'application/json, text/javascript, */*; q=0.01',
    };

    // Header names are case-insensitive, so a caller's header replaces a default whatever its case.
    const mergeHeaders = (...sources) => {
        const merged = {};
        sources.forEach(source => Object.entries(source || {}).forEach(([name, value]) => {
            merged[name.toLowerCase()] = value;
        }));
        return merged;
    };

    const isJsonContentType = headers => /\bjson\b/.test(headers.get('Content-Type') || '');

    // Converts a successful response the way jQuery.ajax does for the dataType. A failure is a parse error.
    const convertForDataType = dataType => response => {
        if (response.status === 204) {
            return {data: undefined};
        }
        const type = dataType ? dataType.toLowerCase() : (isJsonContentType(response.headers) ? 'json' : 'text');
        if (type !== 'json') {
            return {data: response.text};
        }
        try {
            return {data: JSON.parse(response.text)};
        } catch (e) {
            const failure = howspace.ajax.toLegacyResponse({...response, statusText: 'parsererror', data: undefined});
            return {failure};
        }
    };

    const describeNonJson = text => {
        let message = 'Response is not valid JSON.';
        if (text.substring(0, 1) === '{') {
            message += ' First character is { so the request probably got an incomplete response.';
        } else if (text.substring(0, 1) === '<') {
            message += ' First character is < so the response is probably HTML.';
        } else {
            message += ' First character is NOT { or < and response is NOT an empty string. This is interesting...';
        }
        return message;
    };

    const convertJson = response => {
        if (response.status === 204) {
            return {data: undefined};
        }
        if (!response.text) {
            return {data: response.text};
        }
        try {
            return {data: JSON.parse(response.text)};
        } catch (e) {
            return {failure: {status: 406, message: describeNonJson(response.text), response: response.text}};
        }
    };

    const send = (url, options, convert) => {
        const callStack = new Error().stack;
        const method = (options.method || options.type || 'GET').toUpperCase();
        const hasContent = method !== 'GET' && method !== 'HEAD';
        const contentType = options.contentType === undefined ? FORM_CONTENT_TYPE : options.contentType;
        const processData = options.processData !== false;

        const data = options.type === 'POST' && !options.data ? '{}' : options.data;
        const payload = data && processData && typeof data !== 'string' ? howspace.ajax.param(data) : data;

        let query = undefined;
        let body = null;
        if (hasContent) {
            body = payload || null;
            const isForm = String(contentType).startsWith('application/x-www-form-urlencoded');
            if (typeof body === 'string' && processData && isForm) {
                body = body.replace(/%20/g, '+');
            }
        } else if (payload && (processData || typeof payload === 'string')) {
            query = payload;
        }

        const headers = {Accept: ACCEPT_HEADERS[(options.dataType || '*').toLowerCase()] || ACCEPT_HEADERS['*']};
        if ((payload && hasContent && contentType !== false) || options.contentType) {
            headers['Content-Type'] = contentType;
        }

        const reportError = (error, details) => {
            const showErrorNotification = options.showErrorNotification;
            const logError = options.logError;
            howspace.ajax.report(error, {
                notify: !(showErrorNotification === false ||
                    (typeof showErrorNotification === 'function' && showErrorNotification(details) === false)),
                log: !(logError === false || (typeof logError === 'function' && !logError(details))),
                body: data,
                details,
            });
        };

        const finish = (status, statusCodeArg, completeArg) => {
            options.statusCode?.[status]?.(statusCodeArg);
            if (typeof options.complete === 'function') {
                options.complete(completeArg);
            }
        };

        const fail = (error, details) => {
            if (typeof options.error === 'function') {
                options.error(details);
            }
            reportError(error, details);
        };

        const controller = new AbortController();

        const handleResponse = response => {
            if (controller.signal.aborted) {
                return;
            }
            const completeArg = howspace.ajax.toLegacyResponse({
                ...response,
                statusText: response.statusText || 'success',
                data: undefined,
            });
            const {data: result, failure} = convert(response);
            if (failure) {
                const error = new howspace.ajax.Error('Response could not be converted.', {
                    method,
                    url,
                    ...response,
                    kind: 'parse',
                });
                error.callStack = callStack;
                fail(error, failure);
                finish(response.status, failure, completeArg);
                return;
            }
            if (typeof options.success === 'function') {
                options.success(result);
            }
            finish(response.status, result, completeArg);
        };

        const handleError = error => {
            if (controller.signal.aborted || howspace.ajax.isAbort(error)) {
                return;
            }
            if (!(error instanceof howspace.ajax.Error)) {
                // Such as an invalid header value. jQuery.ajax reported these as a failed request too.
                console.error(error);
                error = new howspace.ajax.Error(String(error?.message || error), {method, url, kind: 'network'});
            }
            error.callStack = callStack;
            const details = howspace.ajax.toLegacyResponse(error);
            if (!error.redirecting) {
                fail(error, details);
            }
            finish(details.status, details, details);
        };

        // Callbacks run outside the promise chain, so an exception in one reaches window.onerror and the error log.
        howspace.ajax.request(url, {
            method,
            query,
            body,
            headers: mergeHeaders(headers, options.headers),
            signal: controller.signal,
            responseType: 'text',
            websocket: options.waitForWebsocket,
            notify: false,
            log: false,
            onUploadProgress: options.onUploadProgress,
        }).then(
            response => queueMicrotask(() => handleResponse(response)),
            error => queueMicrotask(() => handleError(error)),
        );

        return {
            abort: () => controller.abort(),
        };
    };

    /**
     * Options: type (or method), data, dataType, contentType, processData, headers, success, error, complete,
     * statusCode, showErrorNotification, logError, waitForWebsocket and onUploadProgress. The callbacks get a single
     * argument. Returns an object with abort(), and an aborted request calls none of the callbacks.
     */
    real.ajax = function (url, options = {}) {
        return send(url, options, convertForDataType(options.dataType));
    };

    real.ajax.json = function (url, options = {}) {
        const settings = {...options};
        if (settings.contentType === undefined) {
            settings.contentType = 'application/json';
        }
        if (settings.dataType === undefined) {
            settings.dataType = 'text';
        }
        return send(url, settings, convertJson);
    };

    real.ajax.html = function (url, options = {}) {
        const successFn = options.success;
        return real.ajax.json(url, {
            ...options,
            success: response => {
                if (typeof successFn === 'function') {
                    successFn(response.html);
                }
            },
        });
    };

    real.ajax.loopErrors = function (response, loopFn) {
        howspace.ajax.getErrors(response).forEach(error => loopFn(error));
    };

    real.ajax.hasErrorKey = function (response, key) {
        return howspace.ajax.getErrors(response).some(error => error?.key === key);
    };

})(real);
